<script setup lang="ts">
import appleIcon from 'assets/apple.svg';

const { t } = useI18n();
const DB = useDbStore();

const route = useRoute();

const props = withDefaults(defineProps<{
  showAs?: 'popup' | 'page';
}>(), {
  showAs: 'popup',
});

const titleText = computed(() => (
  props.showAs === 'page'
    ? t('Welcome_back')
    : t('Login_to_proceed')
));

const emit = defineEmits<{
  (e: 'clickClose'): void;
  (e: 'clickForgotPassword'): void;
  (e: 'successLogin'): void;
}>();

const email = ref('');
const emailValid = ref(false);
const emailError = ref(false);
const responseError = ref(false);

const password = ref('');
const passwordValid = ref(false);
const passwordError = computed(() => {
  // return responseError.value;
  if (responseError.value) return true;

  return !passwordValid.value && password.value.length > 0;
});
const isVerified = computed(() => {
  return emailValid.value && passwordValid.value;
});

const isLoadingPopup = ref(false);

const checkInputElementValidity = (input: HTMLInputElement) => {
  return !!(input.value.length && input.validity.valid);
};

const onPasswordsValidation = (e: FocusEvent | InputEvent) => {
  responseError.value = false;
  passwordValid.value = checkInputElementValidity(e.target! as HTMLInputElement);
};

const onEventValidation = (e: FocusEvent | InputEvent) => {
  onPasswordsValidation(e);

  return checkInputElementValidity(e.target! as HTMLInputElement);
};

const redirectFromUrl = computed(() => {
  if (props.showAs === 'popup') {
    return route.fullPath || '/';
  }
  return route.redirectedFrom?.fullPath || '/';
});

const afterLoginSuccess = async () => {
  isLoadingPopup.value = false;
  emit('successLogin');
  return navigateTo(redirectFromUrl.value);
};

const submit = async () => {
  if (isVerified.value) {
    isLoadingPopup.value = true;
    DB.loginByEmail(email.value, password.value)
      .then((e) => {
        console.info('logged in...');
        return e;
      })
      .then(afterLoginSuccess)
      .catch((e) => {
        isLoadingPopup.value = false;
        emailError.value = true;
        responseError.value = true;
        // passwordError.value = true;
        console.log(' login error...', e);
        return e;
      });
  }
  else {
    console.log('email or password field is not verified...');
  }
};

const onForgotPassword = async () => {
  console.info('forgot password...');
  if (props.showAs === 'page') {
    return navigateTo('/forgot-password');
  }
  else {
    emit('clickForgotPassword');
  }
};

const onGoogle = async () => {
  await DB.loginByGoogle()
    .then((e) => {
      console.info('logged in by Google');
      return e;
    })
    .then(afterLoginSuccess)
    .catch((e) => {
      console.log('login error', e);
      isLoadingPopup.value = false;
    });
};

const onApple = () => {
  isLoadingPopup.value = true;

  DB.loginByApple()
    .then((e) => {
      console.info('logged in by Apple');
      return e;
    })
    .then(afterLoginSuccess)
    .catch((e) => {
      console.log('login error', e);
      isLoadingPopup.value = false;
    });
};

const termsIsOpen = ref(false);
</script>

<template>
  <LoadingCover
    v-if="isLoadingPopup"
  />

  <ModalWindow
    :show-as="showAs"
  >
    <div
      class="flex flex-col items-stretch gap-8 select-none bg-gray-100 rounded-big relative w-[361px] md:w-[423px] max-w-[calc(100vw-32px)] mx-4 p-4 md:p-8"
    >
      <!--      CLOSE BUTTON -->
      <CloseButton
        v-if="showAs !== 'page'"
        size="big"
        class="absolute right-2 top-2"
        @click="$emit('clickClose')"
      />

      <!--      TITLE -->
      <div class="text-h3 font-dedicated text-center">
        {{ titleText }}
      </div>

      <div class="flex flex-col gap-6">
        <!--          EMAIL -->
        <InputField
          v-model.trim="email"
          autocomplete="email"
          type="email"
          :placeholder="t('Email')"
          :is-error="emailError"
          @focusout="emailError = !onEventValidation($event)"
          @input="emailValid = !!onEventValidation($event)"
        />

        <!--          PASSWORD -->
        <div class="flex flex-col flex-nowrap items-stretch gap-2">
          <PasswordInput
            v-model:password="password"
            :password-error="passwordError"
            :placeholder="t('Password')"
            @change="onPasswordsValidation"
            @input="onPasswordsValidation"
          />
          <PasswordError
            v-show="passwordError"
            :password="password"
          />
          <!--            forgot password -->
          <div
            class="text-gray-900 flex justify-end  "
          >
            <ButtonLevel
              type="4"
              :title="t('Forgot_password?')"
              @click="onForgotPassword"
            />
          </div>
        </div>

        <!--    TERMS AND CONDITIONS    -->

        <div class="flex gap-2 items-center">
          <!--          <CheckBox v-model:checked="isAgree" /> -->

          <ModalWindow
            v-if="termsIsOpen"

            show-as="popup"
          >
            <div class="w-9/12 h-[80dvh] overflow-y-hidden flex flex-col gap-4 px-9 py-10 bg-gray-50 absolute  left-1/2 -translate-x-1/2 rounded-big">
              <div class="flex flex-col gap-4 overflow-y-scroll">
                <div class="text-h1 text-gray-900 font-dedicated">
                  {{ t('Distance_contract').toUpperCase() }}
                </div>
                <TermsConditionsContent />
                <div
                  ref="termsScrollRef"
                  class="!h-[1px] shrink-0"
                />
              </div>
              <div class="flex justify-between items-center">
                <ButtonLevel
                  type="3"
                  :title="t('Close')"
                  @click="termsIsOpen = false"
                />
              </div>
            </div>
          </ModalWindow>
          <div class="flex gap-1 items-center">
            <div>{{ t('I_agree_with_login') }}</div>

            <ButtonLevel
              class="underline"
              :title="t('Terms_and_conditions')"
              type="4"
              @click="termsIsOpen = !termsIsOpen"
            />
          </div>
        </div>

        <!--        LOGIN SUBMIT -->
        <ButtonLevel
          :disabled="!isVerified"
          type="1"
          :title="t('Log_in')"
          @click="submit"
        />
        <!--        LINE -->
        <div class="flex flex-nowrap gap-8 items-center justify-stretch">
          <div class="w-full border-b border-gray-800" />
          <div class="text-gray-800 text-big shrink-0 grow-0">
            {{ t('Or_login_with') }}
          </div>
          <div class="w-full border-b border-gray-800" />
        </div>

        <!--        GOOGLE, APPLE  -->
        <div
          class="flex flex-nowrap justify-stretch gap-4"
        >
          <ButtonLevel
            class="h-[42px] w-full"
            type="3"
            title="Google"
            @click="onGoogle"
          >
            <template #prepend>
              <NuxtImg
                format="webp"
                provider="digitalocean"
                width="24"
                height="24"
                src="google24.png"
                class="w-6 h-6"
                alt="google"
              />
            </template>
          </ButtonLevel>
          <ButtonLevel
            class="h-[42px] w-full"
            type="3"
            title="Apple"
            @click="onApple"
          >
            <template #prepend>
              <appleIcon class="w-6 h-6" />
            </template>
          </ButtonLevel>
        </div>

        <!--        SIGN UP LINK -->
        <DontHaveAccountSignup
          v-if="showAs !== 'page'"
          class="pt-24"
        />

        <!--          SPACE -->
        <div
          v-if="showAs === 'page'"
          class="mb-24"
        />
      </div>
    </div>

    <!--        ALREADY HAVE ACCOUNT -->
    <template
      v-if="showAs === 'page'"
      #bottom
    >
      <DontHaveAccountSignup class="mb-8 " />
    </template>
  </ModalWindow>
</template>

<style scoped>

</style>
